/**
 * PeerEvaluationDetailPage Component
 *
 * Editable detail view for peer evaluations (when status is 'concept').
 * Shows evaluator feedback on tasks and competencies with score input.
 * Styled to match evaluation wizard.
 *
 * Story pe-2-5
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePeerEvaluation, useUpdatePeerEvaluation, useDeletePeerEvaluation } from './usePeerEvaluations';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth/AuthContext';

/**
 * Format datum naar nl-NL locale string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Onbekende datum';
  }
  return date.toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Status badge kleuren
 */
const STATUS_STYLES = {
  concept: 'bg-yellow-100 text-yellow-800',
  ingediend: 'bg-green-100 text-green-800',
} as const;

const STATUS_LABELS = {
  concept: 'Concept',
  ingediend: 'Ingediend',
} as const;

/**
 * Score labels en kleuren
 */
const SCORE_LABELS: Record<number, string> = {
  1: 'Onvoldoende',
  2: 'Matig',
  3: 'Voldoende',
  4: 'Goed',
  5: 'Uitzonderlijk',
};

const SCORE_COLORS: Record<number, string> = {
  1: 'bg-red-50 border-red-200 text-red-400 hover:bg-red-100 hover:text-red-600',
  2: 'bg-orange-50 border-orange-200 text-orange-400 hover:bg-orange-100 hover:text-orange-600',
  3: 'bg-yellow-50 border-yellow-200 text-yellow-500 hover:bg-yellow-100 hover:text-yellow-600',
  4: 'bg-lime-50 border-lime-200 text-lime-400 hover:bg-lime-100 hover:text-lime-600',
  5: 'bg-green-50 border-green-200 text-green-400 hover:bg-green-100 hover:text-green-600',
};

const SELECTED_COLORS: Record<number, string> = {
  1: 'bg-red-600 border-red-600 text-white',
  2: 'bg-orange-600 border-orange-600 text-white',
  3: 'bg-yellow-600 border-yellow-600 text-white',
  4: 'bg-lime-600 border-lime-600 text-white',
  5: 'bg-green-600 border-green-600 text-white',
};

function getScoreButtonClass(score: number, isSelected: boolean): string {
  const colors = isSelected ? SELECTED_COLORS : SCORE_COLORS;
  return colors[score] ?? 'bg-gray-50 border-gray-200 text-gray-400';
}

/**
 * Score tooltip component
 */
function ScoreTooltip({
  score,
  label,
  beschrijving,
  isVisible,
}: {
  score: number;
  label: string;
  beschrijving: string | null;
  isVisible: boolean;
}) {
  if (!isVisible) return null;

  return (
    <div
      className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 text-sm bg-gray-800 text-white rounded-lg shadow-lg min-w-[280px] max-w-[360px]"
      role="tooltip"
    >
      <div className="font-semibold mb-1">
        {score} - {label}
      </div>
      {beschrijving ? (
        <div className="text-gray-200 text-xs leading-relaxed whitespace-normal">
          {beschrijving}
        </div>
      ) : (
        <div className="text-gray-400 text-xs italic">
          Geen criteria beschrijving beschikbaar
        </div>
      )}
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-gray-800" />
    </div>
  );
}

interface FormState {
  taskScores: Record<number, number | null>;
  taskRemarks: Record<number, string>;
  competencyScores: Record<number, number | null>;
  competencyRemarks: Record<number, string>;
  generalRemarks: string;
}

export function PeerEvaluationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const peerEvalId = useMemo(() => {
    const parsed = parseInt(id ?? '', 10);
    return isNaN(parsed) ? 0 : parsed;
  }, [id]);

  const { data: peerEvaluation, isLoading, error } = usePeerEvaluation(peerEvalId);
  const updateMutation = useUpdatePeerEvaluation();
  const deleteMutation = useDeletePeerEvaluation();
  const { hasRole } = useAuth();

  // Check if user is HR or admin (can delete)
  const canDelete = hasRole(['hr', 'admin']);

  // Form state
  const [formState, setFormState] = useState<FormState>({
    taskScores: {},
    taskRemarks: {},
    competencyScores: {},
    competencyRemarks: {},
    generalRemarks: '',
  });

  // Hover state for tooltips
  const [hoveredTaskScore, setHoveredTaskScore] = useState<{ taskId: number; score: number } | null>(null);
  const [hoveredCompScore, setHoveredCompScore] = useState<{ compId: number; score: number } | null>(null);

  // Initialize form state from peer evaluation data
  useEffect(() => {
    if (peerEvaluation) {
      const taskScores: Record<number, number | null> = {};
      const taskRemarks: Record<number, string> = {};
      const competencyScores: Record<number, number | null> = {};
      const competencyRemarks: Record<number, string> = {};

      peerEvaluation.tasks.forEach((task) => {
        taskScores[task.taskId] = task.score;
        taskRemarks[task.taskId] = task.remarks || '';
      });

      peerEvaluation.competencies.forEach((comp) => {
        competencyScores[comp.competencyId] = comp.score;
        competencyRemarks[comp.competencyId] = comp.remarks || '';
      });

      setFormState({
        taskScores,
        taskRemarks,
        competencyScores,
        competencyRemarks,
        generalRemarks: peerEvaluation.generalRemarks || '',
      });
    }
  }, [peerEvaluation]);

  // Set document title
  useEffect(() => {
    if (peerEvaluation) {
      document.title = `Peer Evaluatie - ${peerEvaluation.evaluatorName} | Functieloonhuis`;
    } else {
      document.title = 'Peer Evaluatie | Functieloonhuis';
    }
    return () => {
      document.title = 'Functieloonhuis';
    };
  }, [peerEvaluation]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // State for copy link feedback
  const [linkCopied, setLinkCopied] = useState(false);

  // State for save feedback
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Copy public URL to clipboard
  const handleCopyLink = useCallback(async () => {
    if (!peerEvaluation?.token) return;
    const publicUrl = `${window.location.origin}/evaluatie/${peerEvaluation.token}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      alert('Kon link niet kopiëren naar klembord');
    }
  }, [peerEvaluation?.token]);

  // Handlers for form changes
  const handleTaskScoreChange = useCallback((taskId: number, score: number) => {
    setFormState((prev) => ({
      ...prev,
      taskScores: { ...prev.taskScores, [taskId]: score },
    }));
  }, []);

  const handleTaskRemarksChange = useCallback((taskId: number, value: string) => {
    setFormState((prev) => ({
      ...prev,
      taskRemarks: { ...prev.taskRemarks, [taskId]: value },
    }));
  }, []);

  const handleCompetencyScoreChange = useCallback((competencyId: number, score: number) => {
    setFormState((prev) => ({
      ...prev,
      competencyScores: { ...prev.competencyScores, [competencyId]: score },
    }));
  }, []);

  const handleCompetencyRemarksChange = useCallback((competencyId: number, value: string) => {
    setFormState((prev) => ({
      ...prev,
      competencyRemarks: { ...prev.competencyRemarks, [competencyId]: value },
    }));
  }, []);

  const handleGeneralRemarksChange = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, generalRemarks: value }));
  }, []);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!peerEvaluation) return;

    // Clear previous feedback
    setSaveSuccess(false);
    setSaveError(null);

    const taskUpdates = peerEvaluation.tasks.map((task) => ({
      taskId: task.taskId,
      score: formState.taskScores[task.taskId] ?? null,
      remarks: formState.taskRemarks[task.taskId] || undefined,
    }));

    const competencyUpdates = peerEvaluation.competencies.map((comp) => ({
      competencyId: comp.competencyId,
      score: formState.competencyScores[comp.competencyId] ?? null,
      remarks: formState.competencyRemarks[comp.competencyId] || undefined,
    }));

    try {
      await updateMutation.mutateAsync({
        id: peerEvaluation.id,
        data: {
          tasks: taskUpdates,
          competencies: competencyUpdates,
          generalRemarks: formState.generalRemarks || undefined,
        },
      });

      // Show success feedback
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('[PeerEvaluationDetailPage] Save error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Er is een fout opgetreden';
      setSaveError(errorMessage);
      setTimeout(() => setSaveError(null), 5000);
    }
  }, [peerEvaluation, formState, updateMutation]);

  const isEditable = peerEvaluation?.status === 'concept';

  // Delete handler
  const handleDelete = useCallback(async () => {
    if (!peerEvaluation) return;
    try {
      await deleteMutation.mutateAsync({
        id: peerEvaluation.id,
        employeeId: peerEvaluation.employeeId,
      });
      navigate(-1); // Go back to overview
    } catch (error) {
      console.error('[PeerEvaluationDetailPage] Delete error:', error);
      setShowDeleteConfirm(false);
    }
  }, [peerEvaluation, deleteMutation, navigate]);

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div
          className="flex items-center justify-center py-16"
          data-testid="peer-evaluation-detail-loading"
          role="status"
          aria-label="Peer evaluatie laden"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true"></div>
          <span className="ml-3 text-gray-600">Peer evaluatie laden...</span>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error || !peerEvaluation) {
    const errorMessage = error instanceof Error ? error.message : 'Peer evaluatie niet gevonden';
    const isNotFound = errorMessage.includes('niet gevonden') || errorMessage.includes('404');

    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16 px-4" data-testid="peer-evaluation-detail-error">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isNotFound ? 'Peer Evaluatie Niet Gevonden' : 'Toegang Geweigerd'}
            </h1>
            <p className="text-gray-600 mb-6">
              {isNotFound
                ? 'Deze peer evaluatie bestaat niet of is verwijderd.'
                : 'Je hebt geen toegang tot deze peer evaluatie.'}
            </p>
            <button
              onClick={handleBack}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Terug
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 px-4" data-testid="peer-evaluation-detail">
        {/* Back button */}
        <button
          onClick={handleBack}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Terug naar overzicht
        </button>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Peer Evaluatie door {peerEvaluation.evaluatorName}
              </h1>
              <p className="text-gray-600">
                Over: <span className="font-medium">{peerEvaluation.employeeName}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Copy link button (icon only) */}
              <button
                onClick={handleCopyLink}
                className={`inline-flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  linkCopied
                    ? 'text-green-700 bg-green-50 border border-green-300'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
                title={linkCopied ? 'Gekopieerd!' : 'Kopieer link'}
              >
                {linkCopied ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </button>
              {/* Print button (icon only) */}
              <button
                onClick={() => window.open(`/peer-evaluatie/${peerEvaluation.id}/print`, '_blank')}
                className="inline-flex items-center justify-center w-10 h-10 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title="Printen"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
              {/* Delete button (icon only, HR/admin only) */}
              {canDelete && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center justify-center w-10 h-10 text-red-600 bg-white border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
                  title="Verwijderen"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_STYLES[peerEvaluation.status]}`}>
                {STATUS_LABELS[peerEvaluation.status]}
              </span>
            </div>
          </div>

          {/* Metadata */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Aangemaakt:</span>{' '}
                <span className="font-medium text-gray-900">{formatDate(peerEvaluation.createdAt)}</span>
              </div>
              {peerEvaluation.submittedAt && (
                <div>
                  <span className="text-gray-500">Ingediend:</span>{' '}
                  <span className="font-medium text-gray-900">{formatDate(peerEvaluation.submittedAt)}</span>
                </div>
              )}
              <div>
                <span className="text-gray-500">Aangemaakt door:</span>{' '}
                <span className="font-medium text-gray-900">{peerEvaluation.createdByName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Taken Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
            Taken ({peerEvaluation.tasks.length})
          </h2>

          {peerEvaluation.tasks.length === 0 ? (
            <p className="text-gray-500 italic">Geen taken geselecteerd voor deze evaluatie.</p>
          ) : (
            <div className="space-y-4">
              {peerEvaluation.tasks.map((task) => (
                <div
                  key={task.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                  data-testid={`task-item-${task.taskId}`}
                >
                  {/* Task header - wizard style */}
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{task.name}</h3>
                        {task.beschrijving && (
                          <p className="mt-1 text-sm text-gray-500">{task.beschrijving}</p>
                        )}
                      </div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full whitespace-nowrap ml-4">
                        Taak
                      </span>
                    </div>
                  </div>

                  <div className="px-6 py-4 space-y-4">
                    {/* Score input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Jouw beoordeling
                      </label>
                      <div className="flex flex-wrap gap-2" role="radiogroup">
                        {[1, 2, 3, 4, 5].map((score) => {
                          const isSelected = formState.taskScores[task.taskId] === score;
                          const isHovered = hoveredTaskScore?.taskId === task.taskId && hoveredTaskScore.score === score;

                          return (
                            <div key={score} className="relative">
                              <button
                                type="button"
                                onClick={() => isEditable && handleTaskScoreChange(task.taskId, score)}
                                onMouseEnter={() => setHoveredTaskScore({ taskId: task.taskId, score })}
                                onMouseLeave={() => setHoveredTaskScore(null)}
                                disabled={!isEditable}
                                className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${getScoreButtonClass(score, isSelected)} ${!isEditable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                aria-pressed={isSelected}
                              >
                                <span className="font-bold">{score}</span>
                                <span className="hidden sm:inline ml-1 text-xs font-normal">
                                  {SCORE_LABELS[score]}
                                </span>
                              </button>
                              <ScoreTooltip
                                score={score}
                                label={SCORE_LABELS[score] ?? ''}
                                beschrijving={null}
                                isVisible={isHovered}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Toelichting */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Toelichting <span className="text-gray-400 font-normal">(optioneel)</span>
                      </label>
                      <textarea
                        rows={2}
                        value={formState.taskRemarks[task.taskId] || ''}
                        onChange={(e) => handleTaskRemarksChange(task.taskId, e.target.value)}
                        disabled={!isEditable}
                        placeholder="Geef een toelichting op je beoordeling..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Competenties Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
            Competenties ({peerEvaluation.competencies.length})
          </h2>

          {peerEvaluation.competencies.length === 0 ? (
            <p className="text-gray-500 italic">Geen competenties geselecteerd voor deze evaluatie.</p>
          ) : (
            <div className="space-y-4">
              {peerEvaluation.competencies.map((competency) => {
                const badgeStyles = competency.type === 'basis'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700';
                const badgeLabel = competency.type === 'basis' ? 'Basiscompetentie' : 'Vakcompetentie';

                return (
                  <div
                    key={competency.id}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                    data-testid={`competency-item-${competency.competencyId}`}
                  >
                    {/* Competency header - wizard style */}
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{competency.name}</h3>
                          {competency.beschrijving && (
                            <p className="mt-1 text-sm text-gray-500">{competency.beschrijving}</p>
                          )}
                        </div>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full whitespace-nowrap ml-4 ${badgeStyles}`}>
                          {badgeLabel}
                        </span>
                      </div>
                    </div>

                    <div className="px-6 py-4 space-y-4">
                      {/* Score input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Jouw beoordeling
                        </label>
                        <div className="flex flex-wrap gap-2" role="radiogroup">
                          {[1, 2, 3, 4, 5].map((score) => {
                            const isSelected = formState.competencyScores[competency.competencyId] === score;
                            const isHovered = hoveredCompScore?.compId === competency.competencyId && hoveredCompScore.score === score;

                            return (
                              <div key={score} className="relative">
                                <button
                                  type="button"
                                  onClick={() => isEditable && handleCompetencyScoreChange(competency.competencyId, score)}
                                  onMouseEnter={() => setHoveredCompScore({ compId: competency.competencyId, score })}
                                  onMouseLeave={() => setHoveredCompScore(null)}
                                  disabled={!isEditable}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${getScoreButtonClass(score, isSelected)} ${!isEditable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                  aria-pressed={isSelected}
                                >
                                  <span className="font-bold">{score}</span>
                                  <span className="hidden sm:inline ml-1 text-xs font-normal">
                                    {SCORE_LABELS[score]}
                                  </span>
                                </button>
                                <ScoreTooltip
                                  score={score}
                                  label={SCORE_LABELS[score] ?? ''}
                                  beschrijving={null}
                                  isVisible={isHovered}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Toelichting */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Toelichting <span className="text-gray-400 font-normal">(optioneel)</span>
                        </label>
                        <textarea
                          rows={2}
                          value={formState.competencyRemarks[competency.competencyId] || ''}
                          onChange={(e) => handleCompetencyRemarksChange(competency.competencyId, e.target.value)}
                          disabled={!isEditable}
                          placeholder="Geef een toelichting op je beoordeling..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Algemene Opmerkingen Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
            Algemene Opmerkingen
          </h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aanvullende feedback <span className="text-gray-400 font-normal">(optioneel)</span>
            </label>
            <textarea
              rows={4}
              value={formState.generalRemarks}
              onChange={(e) => handleGeneralRemarksChange(e.target.value)}
              disabled={!isEditable}
              placeholder="Deel hier eventuele algemene observaties of aanvullende feedback..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>
        </div>

        {/* Save button (only when editable) */}
        {isEditable && (
          <div className="flex flex-col items-end gap-2">
            {/* Success message */}
            {saveSuccess && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Wijzigingen opgeslagen
              </div>
            )}

            {/* Error message */}
            {saveError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {saveError}
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className={`px-6 py-2 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                saveSuccess
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {updateMutation.isPending ? 'Opslaan...' : saveSuccess ? 'Opgeslagen!' : 'Opslaan'}
            </button>
          </div>
        )}

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Peer evaluatie verwijderen?
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Weet je zeker dat je deze peer evaluatie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteMutation.isPending ? 'Verwijderen...' : 'Verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
